/* eslint-disable */

import React, { useEffect, useMemo, useState } from "react";
import "./SuperAdminSchools.css";
import {
  addSchoolAccountNote,
  deleteSchoolAccountPermanently,
  subscribeToSchoolLifecycleHistory,
  subscribeToSchools,
  updateSchoolAccountProfile,
  updateSchoolLifecycleStatus,
} from "../services/schoolsService";

const LOGO_URL = "https://presentplus.uk/presentplus-logo-white.png";

const STATUS_OPTIONS = [
  { value: "live", label: "Live" },
  { value: "paused", label: "Paused" },
  { value: "suspended", label: "Suspended" },
  { value: "archived", label: "Archived" },
];

const STATUS_COPY = {
  live: "The school account is active.",
  paused: "Use for a temporary commercial or operational pause.",
  suspended: "Use when CoreLogic must restrict the customer account pending review.",
  archived: "Use when the school has left PresentPlus but its account history must be retained.",
};

function valueFrom(...values) {
  return values.find(
    (value) =>
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
  );
}

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value._seconds === "number") return new Date(value._seconds * 1000);

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(value, includeTime = false) {
  const date = toDate(value);
  if (!date) return "—";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : {}),
    timeZone: "Europe/London",
  });
}

function normaliseLifecycleStatus(school) {
  const explicit = String(
    valueFrom(
      school.lifecycleStatus,
      school.schoolStatus,
      school.tenantStatus,
      school.status
    ) || ""
  )
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");

  if (["live", "active", "activated"].includes(explicit)) return "live";
  if (["paused", "pause"].includes(explicit)) return "paused";
  if (["suspended", "suspend", "blocked"].includes(explicit)) return "suspended";
  if (["archived", "archive", "inactive", "deactivated"].includes(explicit)) {
    return "archived";
  }

  return valueFrom(
    school.activatedAt,
    school.adminActivatedAt,
    school.createdAt
  )
    ? "live"
    : "pending";
}

function statusLabel(status) {
  return (
    STATUS_OPTIONS.find((option) => option.value === status)?.label ||
    "Pending Setup"
  );
}

function readAdmin(school) {
  const invite = school.adminInvite || {};
  const admin = school.admin || school.primaryAdmin || {};

  return {
    name:
      valueFrom(
        admin.fullName,
        admin.name,
        school.adminFullName,
        school.adminName,
        invite.fullName,
        invite.name,
        invite.recipientName
      ) || "Administrator not recorded",
    email:
      valueFrom(
        admin.email,
        school.adminEmail,
        school.primaryAdminEmail,
        invite.email,
        invite.recipientEmail
      ) || "No administrator email",
  };
}

function readPlan(school) {
  return String(
    valueFrom(
      school.planName,
      school.subscriptionPlan,
      school.plan,
      school.subscription?.plan
    ) || "PresentPlus"
  )
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function isThisMonth(value) {
  const date = toDate(value);
  if (!date) return false;

  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth()
  );
}

function dateInputValue(value) {
  const date = toDate(value);
  if (!date) return "";
  return date.toISOString().slice(0, 10);
}

function schoolNameOf(school) {
  return (
    valueFrom(school.schoolName, school.name, school.displayName) ||
    "Unnamed school"
  );
}

export default function SuperAdminSchools() {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [profileDraft, setProfileDraft] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [lifecycleDialog, setLifecycleDialog] = useState(null);
  const [lifecycleReason, setLifecycleReason] = useState("");
  const [lifecycleNotes, setLifecycleNotes] = useState("");
  const [savingLifecycle, setSavingLifecycle] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deletingSchool, setDeletingSchool] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToSchools(
      (rows) => {
        setSchools(rows);
        setLoading(false);
      },
      (nextError) => {
        console.error("Could not load schools:", nextError);
        setError(nextError?.message || "Could not load school accounts.");
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const preparedSchools = useMemo(
    () =>
      schools.map((school) => ({
        ...school,
        lifecycleStatus: normaliseLifecycleStatus(school),
        adminDisplay: readAdmin(school),
        planDisplay: readPlan(school),
      })),
    [schools]
  );

  const selectedSchool = useMemo(
    () =>
      preparedSchools.find((school) => school.id === selectedSchoolId) || null,
    [preparedSchools, selectedSchoolId]
  );

  useEffect(() => {
    if (!selectedSchool) {
      setProfileDraft(null);
      setHistory([]);
      return undefined;
    }

    setProfileDraft({
      plan: selectedSchool.planDisplay === "PresentPlus" ? "" : selectedSchool.planDisplay,
      renewalDate: dateInputValue(
        valueFrom(
          selectedSchool.renewalDate,
          selectedSchool.subscription?.renewalDate,
          selectedSchool.contract?.renewalDate
        )
      ),
      onboardingStatus:
        valueFrom(selectedSchool.onboardingStatus, selectedSchool.onboarding?.status) ||
        "not_started",
      billingStatus:
        valueFrom(selectedSchool.billingStatus, selectedSchool.billing?.status) ||
        "not_set",
      supportStatus:
        valueFrom(selectedSchool.supportStatus, selectedSchool.support?.status) ||
        "normal",
      accountOwner:
        valueFrom(selectedSchool.accountOwner, selectedSchool.customerSuccessOwner) || "",
    });

    setHistoryLoading(true);
    const unsubscribe = subscribeToSchoolLifecycleHistory(
      selectedSchool.id,
      (rows) => {
        setHistory(rows);
        setHistoryLoading(false);
      },
      (nextError) => {
        console.error("Could not load school lifecycle history:", nextError);
        setHistory([]);
        setHistoryLoading(false);
      }
    );

    return unsubscribe;
  }, [selectedSchool?.id]);

  const statistics = useMemo(() => {
    const result = {
      total: preparedSchools.length,
      live: 0,
      paused: 0,
      suspended: 0,
      archived: 0,
      pending: 0,
      activatedThisMonth: 0,
    };

    preparedSchools.forEach((school) => {
      if (result[school.lifecycleStatus] !== undefined) {
        result[school.lifecycleStatus] += 1;
      }

      if (
        isThisMonth(
          valueFrom(
            school.activatedAt,
            school.adminActivatedAt,
            school.createdAt
          )
        )
      ) {
        result.activatedThisMonth += 1;
      }
    });

    return result;
  }, [preparedSchools]);

  const visibleSchools = useMemo(() => {
    const query = search.trim().toLowerCase();

    return preparedSchools.filter((school) => {
      if (filter !== "all" && school.lifecycleStatus !== filter) return false;
      if (!query) return true;

      return [
        schoolNameOf(school),
        school.id,
        school.schoolId,
        school.adminDisplay.name,
        school.adminDisplay.email,
        school.planDisplay,
        statusLabel(school.lifecycleStatus),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [preparedSchools, search, filter]);

  function closeProfile() {
    setSelectedSchoolId("");
    setLifecycleDialog(null);
    setLifecycleReason("");
    setLifecycleNotes("");
    setNewNote("");
    setDeleteDialogOpen(false);
    setDeleteConfirmation("");
  }

  async function saveProfile() {
    if (!selectedSchool || !profileDraft) return;

    setSavingProfile(true);
    setError("");
    setNotice("");

    try {
      await updateSchoolAccountProfile(selectedSchool.id, {
        plan: profileDraft.plan.trim(),
        renewalDate: profileDraft.renewalDate || null,
        onboardingStatus: profileDraft.onboardingStatus,
        billingStatus: profileDraft.billingStatus,
        supportStatus: profileDraft.supportStatus,
        accountOwner: profileDraft.accountOwner.trim(),
      });

      setNotice(`${schoolNameOf(selectedSchool)} account profile was saved.`);
    } catch (nextError) {
      console.error(nextError);
      setError(nextError?.message || "Could not save the school account profile.");
    } finally {
      setSavingProfile(false);
    }
  }

  function requestStatusChange(nextStatus) {
    setLifecycleReason("");
    setLifecycleNotes("");
    setLifecycleDialog(nextStatus);
  }

  async function confirmStatusChange() {
    if (!selectedSchool || !lifecycleDialog) return;

    if (lifecycleDialog !== "live" && !lifecycleReason.trim()) {
      setError("Please enter a reason for this lifecycle change.");
      return;
    }

    setSavingLifecycle(true);
    setError("");
    setNotice("");

    try {
      await updateSchoolLifecycleStatus(selectedSchool.id, {
        nextStatus: lifecycleDialog,
        reason: lifecycleReason.trim(),
        notes: lifecycleNotes.trim(),
        previousStatus: selectedSchool.lifecycleStatus,
        schoolName: schoolNameOf(selectedSchool),
      });

      setNotice(
        `${schoolNameOf(selectedSchool)} is now ${statusLabel(
          lifecycleDialog
        ).toLowerCase()}.`
      );
      setLifecycleDialog(null);
      setLifecycleReason("");
      setLifecycleNotes("");
    } catch (nextError) {
      console.error(nextError);
      setError(nextError?.message || "Could not update the school lifecycle.");
    } finally {
      setSavingLifecycle(false);
    }
  }

  async function saveInternalNote() {
    if (!selectedSchool || !newNote.trim()) return;

    setSavingNote(true);
    setError("");
    setNotice("");

    try {
      await addSchoolAccountNote(selectedSchool.id, {
        note: newNote.trim(),
        schoolName: schoolNameOf(selectedSchool),
      });
      setNewNote("");
      setNotice("Internal account note added.");
    } catch (nextError) {
      console.error(nextError);
      setError(nextError?.message || "Could not add the internal note.");
    } finally {
      setSavingNote(false);
    }
  }


  async function confirmPermanentDelete() {
    if (!selectedSchool) return;

    const schoolName = schoolNameOf(selectedSchool);
    if (deleteConfirmation.trim() !== schoolName) {
      setError(`Type "${schoolName}" exactly to confirm permanent deletion.`);
      return;
    }

    setDeletingSchool(true);
    setError("");
    setNotice("");

    try {
      await deleteSchoolAccountPermanently(selectedSchool.id, {
        schoolName,
      });

      setSelectedSchoolId("");
      setDeleteDialogOpen(false);
      setDeleteConfirmation("");
      setNotice(`${schoolName} was permanently deleted.`);
    } catch (nextError) {
      console.error(nextError);
      setError(
        nextError?.message ||
          "Could not permanently delete the school account."
      );
    } finally {
      setDeletingSchool(false);
    }
  }

  return (
    <div className="pp-schools-page">
      <section className="pp-schools-hero">
        <div className="pp-schools-hero-copy">
          <div className="pp-schools-brand">
            <span className="pp-schools-logo">
              <img src={LOGO_URL} alt="" />
            </span>
            <span>Customer Account Centre</span>
          </div>

          <h1>Customer accounts, without entering school operations.</h1>
          <p>
            CoreLogic manages subscriptions, agreements, onboarding and account
            lifecycle here. Student, parent, teacher, attendance and school-content
            data are intentionally excluded.
          </p>
        </div>

        <div className="pp-schools-hero-summary" aria-label="Customer account summary">
          <div className="pp-schools-hero-metric">
            <span>Live school accounts</span>
            <strong>{loading ? "…" : statistics.live}</strong>
            <small>Currently active customers</small>
          </div>

          <div className="pp-schools-hero-metric">
            <span>Activated this month</span>
            <strong>{loading ? "…" : statistics.activatedThisMonth}</strong>
            <small>Newly activated accounts</small>
          </div>
        </div>
      </section>

      {error ? (
        <div className="pp-schools-message is-error">{error}</div>
      ) : null}
      {notice ? (
        <div className="pp-schools-message is-success">{notice}</div>
      ) : null}

      <section className="pp-schools-stats">
        <StatCard label="Total Accounts" value={loading ? "…" : statistics.total} />
        <StatCard label="Live" value={loading ? "…" : statistics.live} tone="live" />
        <StatCard
          label="Paused / Suspended"
          value={
            loading ? "…" : statistics.paused + statistics.suspended
          }
          tone="pending"
        />
        <StatCard
          label="Archived"
          value={loading ? "…" : statistics.archived}
          tone="month"
        />
      </section>

      <section className="pp-schools-panel">
        <div className="pp-schools-toolbar">
          <div>
            <span className="pp-schools-eyebrow">Customer directory</span>
            <h2>School accounts</h2>
            <p>Only commercial and account-administration information is shown.</p>
          </div>

          <div className="pp-schools-controls">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search school, administrator or email..."
              aria-label="Search school accounts"
            />

            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              aria-label="Filter school accounts by status"
            >
              <option value="all">All statuses</option>
              <option value="live">Live</option>
              <option value="pending">Pending Setup</option>
              <option value="paused">Paused</option>
              <option value="suspended">Suspended</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="pp-schools-empty">Loading school accounts...</div>
        ) : visibleSchools.length === 0 ? (
          <div className="pp-schools-empty">
            {schools.length === 0
              ? "No school account records were found."
              : "No school accounts match this search or filter."}
          </div>
        ) : (
          <div className="pp-schools-table-wrap">
            <table className="pp-schools-table">
              <thead>
                <tr>
                  <th>School Account</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Main Administrator</th>
                  <th>Activated</th>
                  <th>Renewal</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>

              <tbody>
                {visibleSchools.map((school) => {
                  const schoolName = schoolNameOf(school);
                  const schoolId = valueFrom(school.schoolId, school.id) || "—";
                  const activatedAt = valueFrom(
                    school.activatedAt,
                    school.adminActivatedAt,
                    school.createdAt
                  );
                  const renewalDate = valueFrom(
                    school.renewalDate,
                    school.subscription?.renewalDate,
                    school.contract?.renewalDate
                  );

                  return (
                    <tr key={school.id}>
                      <td>
                        <div className="pp-school-name-cell pp-school-name-cell--clean">
                          <span className="pp-school-name-copy">
                            <strong className="pp-school-name">{schoolName}</strong>
                            <small className="pp-school-id">{schoolId}</small>
                          </span>
                        </div>
                      </td>

                      <td>
                        <span
                          className={`pp-school-status is-${school.lifecycleStatus}`}
                        >
                          {statusLabel(school.lifecycleStatus)}
                        </span>
                      </td>

                      <td>{school.planDisplay}</td>

                      <td>
                        <div className="pp-school-admin">
                          <strong className="pp-school-admin-name">{school.adminDisplay.name}</strong>
                          <small className="pp-school-admin-email">{school.adminDisplay.email}</small>
                        </div>
                      </td>

                      <td>{formatDate(activatedAt)}</td>
                      <td>{formatDate(renewalDate)}</td>

                      <td>
                        <button
                          type="button"
                          className="pp-school-view-button"
                          onClick={() => {
                            setNotice("");
                            setError("");
                            setSelectedSchoolId(school.id);
                          }}
                        >
                          Manage Account
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selectedSchool && profileDraft ? (
        <div
          className="pp-account-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeProfile();
          }}
        >
          <aside
            className="pp-account-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={`${schoolNameOf(selectedSchool)} account profile`}
          >
            <header className="pp-account-header">
              <div>
                <span className="pp-schools-eyebrow">School account profile</span>
                <h2>{schoolNameOf(selectedSchool)}</h2>
                <div className="pp-account-header-meta">
                  <span
                    className={`pp-school-status is-${selectedSchool.lifecycleStatus}`}
                  >
                    {statusLabel(selectedSchool.lifecycleStatus)}
                  </span>
                  <span>{valueFrom(selectedSchool.schoolId, selectedSchool.id)}</span>
                </div>
              </div>

              <button
                type="button"
                className="pp-account-close"
                onClick={closeProfile}
                aria-label="Close school account profile"
              >
                ×
              </button>
            </header>

            <div className="pp-account-body">
              <section className="pp-account-section">
                <div className="pp-account-section-heading">
                  <div>
                    <span className="pp-schools-eyebrow">CoreLogic record</span>
                    <h3>Account details</h3>
                  </div>
                  <button
                    type="button"
                    className="pp-account-primary-button"
                    onClick={saveProfile}
                    disabled={savingProfile}
                  >
                    {savingProfile ? "Saving..." : "Save Account"}
                  </button>
                </div>

                <div className="pp-account-fields">
                  <label>
                    <span>Subscription plan</span>
                    <input
                      value={profileDraft.plan}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          plan: event.target.value,
                        }))
                      }
                      placeholder="e.g. Professional"
                    />
                  </label>

                  <label>
                    <span>Renewal date</span>
                    <input
                      type="date"
                      value={profileDraft.renewalDate}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          renewalDate: event.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    <span>Onboarding</span>
                    <select
                      value={profileDraft.onboardingStatus}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          onboardingStatus: event.target.value,
                        }))
                      }
                    >
                      <option value="not_started">Not started</option>
                      <option value="in_progress">In progress</option>
                      <option value="completed">Completed</option>
                      <option value="on_hold">On hold</option>
                    </select>
                  </label>

                  <label>
                    <span>Billing status</span>
                    <select
                      value={profileDraft.billingStatus}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          billingStatus: event.target.value,
                        }))
                      }
                    >
                      <option value="not_set">Not set</option>
                      <option value="trial">Trial</option>
                      <option value="current">Current</option>
                      <option value="payment_due">Payment due</option>
                      <option value="overdue">Overdue</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </label>

                  <label>
                    <span>Support status</span>
                    <select
                      value={profileDraft.supportStatus}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          supportStatus: event.target.value,
                        }))
                      }
                    >
                      <option value="normal">Normal</option>
                      <option value="needs_attention">Needs attention</option>
                      <option value="escalated">Escalated</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </label>

                  <label>
                    <span>CoreLogic account owner</span>
                    <input
                      value={profileDraft.accountOwner}
                      onChange={(event) =>
                        setProfileDraft((current) => ({
                          ...current,
                          accountOwner: event.target.value,
                        }))
                      }
                      placeholder="e.g. Daniel"
                    />
                  </label>
                </div>
              </section>

              <section className="pp-account-section">
                <span className="pp-schools-eyebrow">Customer contact</span>
                <h3>Main administrator</h3>

                <div className="pp-account-readonly-grid">
                  <div>
                    <span>Name</span>
                    <strong>{selectedSchool.adminDisplay.name}</strong>
                  </div>
                  <div>
                    <span>Email</span>
                    <strong>{selectedSchool.adminDisplay.email}</strong>
                  </div>
                  <div>
                    <span>Activated</span>
                    <strong>
                      {formatDate(
                        valueFrom(
                          selectedSchool.activatedAt,
                          selectedSchool.adminActivatedAt,
                          selectedSchool.createdAt
                        )
                      )}
                    </strong>
                  </div>
                  <div>
                    <span>Agreement reference</span>
                    <strong>
                      {valueFrom(
                        selectedSchool.agreementId,
                        selectedSchool.agreementInviteId
                      ) || "Not linked"}
                    </strong>
                  </div>
                </div>
              </section>

              <section className="pp-account-section">
                <span className="pp-schools-eyebrow">Lifecycle controls</span>
                <h3>Account status</h3>
                <p className="pp-account-section-copy">
                  These controls manage the customer account record. No school
                  operational records are opened or deleted.
                </p>

                <div className="pp-lifecycle-actions">
                  {STATUS_OPTIONS.filter(
                    (option) => option.value !== selectedSchool.lifecycleStatus
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`pp-lifecycle-button is-${option.value}`}
                      onClick={() => requestStatusChange(option.value)}
                    >
                      {option.value === "live"
                        ? "Reactivate"
                        : option.value === "archived"
                        ? "Archive Account"
                        : `${option.label} Account`}
                    </button>
                  ))}
                </div>

                <div className="pp-enforcement-note">
                  <strong>Important security note</strong>
                  <span>
                    This step records and audits lifecycle status. Blocking school
                    portal logins and database access must be enforced separately
                    across authentication and Firestore rules before using Pause or
                    Suspend as a technical access lock.
                  </span>
                </div>
              </section>

              <section className="pp-account-section">
                <span className="pp-schools-eyebrow">Internal only</span>
                <h3>Account notes</h3>

                <textarea
                  value={newNote}
                  onChange={(event) => setNewNote(event.target.value)}
                  rows={4}
                  placeholder="Add a private CoreLogic note about this customer account..."
                />

                <button
                  type="button"
                  className="pp-account-secondary-button"
                  onClick={saveInternalNote}
                  disabled={savingNote || !newNote.trim()}
                >
                  {savingNote ? "Adding..." : "Add Internal Note"}
                </button>
              </section>

              <section className="pp-account-section">
                <span className="pp-schools-eyebrow">Account history</span>
                <h3>Lifecycle and notes</h3>

                {historyLoading ? (
                  <div className="pp-account-history-empty">Loading history...</div>
                ) : history.length === 0 ? (
                  <div className="pp-account-history-empty">
                    No lifecycle actions or internal notes have been recorded.
                  </div>
                ) : (
                  <div className="pp-account-history">
                    {history.map((entry) => (
                      <article key={entry.id}>
                        <span
                          className={`pp-history-icon is-${entry.type || "note"}`}
                        />
                        <div>
                          <strong>
                            {entry.type === "lifecycle"
                              ? `${statusLabel(entry.previousStatus)} → ${statusLabel(
                                  entry.nextStatus
                                )}`
                              : "Internal note"}
                          </strong>
                          {entry.reason ? <p>{entry.reason}</p> : null}
                          {entry.notes || entry.note ? (
                            <p>{entry.notes || entry.note}</p>
                          ) : null}
                          <small>
                            {formatDate(entry.createdAt, true)}
                            {entry.createdByEmail
                              ? ` · ${entry.createdByEmail}`
                              : ""}
                          </small>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>

              <section className="pp-account-section pp-danger-zone">
                <span className="pp-schools-eyebrow">Danger zone</span>
                <h3>Delete test account permanently</h3>
                <p className="pp-account-section-copy">
                  Use this only for test, duplicate or incorrectly created school
                  accounts. This removes the school account and its CoreLogic account
                  audit entries. It cannot be undone.
                </p>

                <button
                  type="button"
                  className="pp-delete-account-button"
                  onClick={() => {
                    setError("");
                    setDeleteConfirmation("");
                    setDeleteDialogOpen(true);
                  }}
                >
                  Delete Account Permanently
                </button>
              </section>
            </div>
          </aside>
        </div>
      ) : null}


      {selectedSchool && deleteDialogOpen ? (
        <div className="pp-confirm-overlay pp-delete-confirm-overlay" role="presentation">
          <section
            className="pp-confirm-dialog pp-delete-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={`Delete ${schoolNameOf(selectedSchool)} permanently`}
          >
            <span className="pp-danger-eyebrow">Permanent deletion</span>
            <h3>Delete {schoolNameOf(selectedSchool)}?</h3>
            <p>
              This will permanently remove the school account from the Customer
              Accounts directory and delete its CoreLogic account audit history.
              This action cannot be undone.
            </p>

            <div className="pp-delete-warning">
              This does not automatically delete Firebase Authentication users,
              Storage files or school operational collections.
            </div>

            <label>
              <span>
                Type <strong>{schoolNameOf(selectedSchool)}</strong> to confirm
              </span>
              <input
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                placeholder={schoolNameOf(selectedSchool)}
                autoFocus
                autoComplete="off"
              />
            </label>

            <div className="pp-confirm-actions">
              <button
                type="button"
                className="pp-account-secondary-button"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setDeleteConfirmation("");
                }}
                disabled={deletingSchool}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pp-delete-confirm-button"
                onClick={confirmPermanentDelete}
                disabled={
                  deletingSchool ||
                  deleteConfirmation.trim() !== schoolNameOf(selectedSchool)
                }
              >
                {deletingSchool ? "Deleting..." : "Delete Permanently"}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {selectedSchool && lifecycleDialog ? (
        <div className="pp-confirm-overlay" role="presentation">
          <section
            className="pp-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={`${statusLabel(lifecycleDialog)} school account`}
          >
            <span className="pp-schools-eyebrow">Confirm lifecycle action</span>
            <h3>
              {lifecycleDialog === "live"
                ? "Reactivate"
                : statusLabel(lifecycleDialog)}{" "}
              {schoolNameOf(selectedSchool)}?
            </h3>
            <p>{STATUS_COPY[lifecycleDialog]}</p>

            {lifecycleDialog !== "live" ? (
              <label>
                <span>Reason *</span>
                <input
                  value={lifecycleReason}
                  onChange={(event) => setLifecycleReason(event.target.value)}
                  placeholder="Why is this account status changing?"
                  autoFocus
                />
              </label>
            ) : null}

            <label>
              <span>Additional internal notes</span>
              <textarea
                value={lifecycleNotes}
                onChange={(event) => setLifecycleNotes(event.target.value)}
                rows={4}
                placeholder="Optional context for the audit history..."
              />
            </label>

            <div className="pp-confirm-actions">
              <button
                type="button"
                className="pp-account-secondary-button"
                onClick={() => setLifecycleDialog(null)}
                disabled={savingLifecycle}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`pp-lifecycle-button is-${lifecycleDialog}`}
                onClick={confirmStatusChange}
                disabled={savingLifecycle}
              >
                {savingLifecycle ? "Saving..." : "Confirm Change"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function StatCard({ label, value, tone = "default" }) {
  return (
    <article className={`pp-schools-stat is-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}
